import JSONStableStringify from "json-stable-stringify";

/**
 * Produce a short hash that represents the canonicalized variables payload.
 * Used to identify and track problematic variable combinations.
 * Works isomorphically in both Node.js and browser environments.
 *
 * Uses json-stable-stringify to ensure deterministic string representation
 * regardless of object key order.
 *
 * @param vars - The GraphQL variables object or array to hash
 * @returns A 16-character hash of the variables, or "vh_err" on error
 * @example
 * await varsHash({userId: 123, limit: 10}) // "a1b2c3d4e5f6g7h8"
 */
export async function varsHash(
  vars: Record<string, unknown> | unknown[],
): Promise<string> {
  try {
    const s = JSONStableStringify(vars);
    if (!s) {
      return "vh_err";
    }

    // Node.js environment - use native crypto for SHA-1
    if (typeof window === "undefined") {
      const nodeCrypto = await import("crypto");
      return nodeCrypto.default
        .createHash("sha1")
        .update(s)
        .digest("hex")
        .slice(0, 16);
    }

    // Browser environment - use simple deterministic hash (djb2 variant)
    // Note: This doesn't need to match the server hash exactly, just be consistent
    let hash = 5381;
    for (let i = 0; i < s.length; i++) {
      hash = (hash * 33) ^ s.charCodeAt(i);
    }
    return Math.abs(hash).toString(16).padStart(16, "0").slice(0, 16);
  } catch {
    return "vh_err";
  }
}

/** Hash method that works either using webcrypto or node crypto module */
export default async function createHash({
  text,
  hashType = "SHA-256",
}: {
  text: string;
  hashType?: "SHA-256" | "SHA-1";
}): Promise<string> {
  const lowerCaseNoDashHashType = hashType.toLowerCase().replace(/-/g, "");

  if (typeof crypto === "undefined") {
    const nodeCrypto = await import("crypto");
    return nodeCrypto
      .createHash(lowerCaseNoDashHashType)
      .update(text)
      .digest("hex");
  }

  try {
    const msgUint8 = new TextEncoder().encode(text); // encode as (utf-8) Uint8Array
    const hashBuffer = await crypto.subtle.digest(hashType, msgUint8); // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(""); // convert bytes to hex string
    return hashHex;
  } catch (error) {
    console.warn("Failed to create hash", error);
  }

  return "";
}
